<script lang="ts">
  import BrandMark from '../../components/BrandMark.svelte'
  import ThemeToggle from '../../components/ThemeToggle.svelte'
  import { apiBaseUrl } from '$lib/api/base-url'

  let name = $state('')
  let company = $state('')
  let email = $state('')
  let phone = $state('')
  let portfolioSize = $state('')
  let message = $state('')
  let website = $state('') // honeypot
  let phase = $state<'form' | 'sending' | 'done' | 'error'>('form')

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const canSubmit = $derived(name.trim() !== '' && company.trim() !== '' && EMAIL_RE.test(email))

  async function submit(e: Event): Promise<void> {
    e.preventDefault()
    if (!canSubmit) return
    phase = 'sending'
    try {
      const res = await fetch(`${apiBaseUrl()}/public/provider-contact`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          company: company.trim(),
          email: email.trim(),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(portfolioSize.trim() ? { portfolioSize: portfolioSize.trim() } : {}),
          ...(message.trim() ? { message: message.trim() } : {}),
          ...(website ? { website } : {}),
        }),
      })
      phase = res.ok ? 'done' : 'error'
    } catch {
      phase = 'error'
    }
  }
</script>

<div class="providers">
  <header class="site">
    <div class="wrap bar">
      <a class="brand" href="/"
        ><BrandMark size={30} decorative /><span>Rent<span class="accent">Ready</span>Pet</span></a
      >
      <div class="bar-right">
        <a class="btn ghost" href="/register">Register your pet</a>
        <ThemeToggle />
      </div>
    </div>
  </header>

  <main class="wrap" id="main-content" tabindex="-1">
    <section class="hero">
      <p class="eyebrow">For housing providers</p>
      <h1>A fair, documented pet and assistance-animal process.</h1>
      <p class="lead">
        RentReadyPet gives your team one consistent, well-documented workflow for every applicant.
        We are decision-support, never the decision-maker, and we never sell your applicants' data.
      </p>
    </section>

    {#if phase === 'done'}
      <p class="ok" role="status">Thanks. We'll be in touch soon.</p>
    {:else}
      <form class="card" onsubmit={submit} novalidate>
        {#if phase === 'error'}<p class="err">Something went wrong. Please try again.</p>{/if}
        <label>Name<input bind:value={name} required autocomplete="name" /></label>
        <label>Company<input bind:value={company} required autocomplete="organization" /></label>
        <label
          >Work email<input type="email" bind:value={email} required autocomplete="email" /></label
        >
        <label>Phone (optional)<input bind:value={phone} autocomplete="tel" /></label>
        <label
          >Portfolio size (optional)<input
            bind:value={portfolioSize}
            placeholder="e.g. 50-100 units"
          /></label
        >
        <label>Message (optional)<textarea bind:value={message} rows="4"></textarea></label>
        <label class="hp" aria-hidden="true"
          >Website<input tabindex="-1" autocomplete="off" bind:value={website} /></label
        >
        <button class="btn" type="submit" disabled={!canSubmit || phase === 'sending'}>
          {phase === 'sending' ? 'Sending...' : 'Get in touch'}
        </button>
      </form>
    {/if}
  </main>
</div>

<style>
  .providers {
    --l-ground: #fbf6ee;
    --l-panel: #ffffff;
    --l-ink: #2a211b;
    --l-muted: #6f6154;
    --l-line: #ecdfcd;
    background: var(--l-ground);
    color: var(--l-ink);
    min-height: 100vh;
  }
  @media (prefers-color-scheme: dark) {
    .providers {
      --l-ground: var(--surface);
      --l-panel: var(--surface-2);
      --l-ink: var(--ink-strong);
      --l-muted: var(--muted);
      --l-line: var(--line);
    }
  }
  :root[data-theme='dark'] .providers {
    --l-ground: var(--surface);
    --l-panel: var(--surface-2);
    --l-ink: var(--ink-strong);
    --l-muted: var(--muted);
    --l-line: var(--line);
  }
  :root[data-theme='light'] .providers {
    --l-ground: #fbf6ee;
    --l-panel: #ffffff;
    --l-ink: #2a211b;
    --l-muted: #6f6154;
    --l-line: #ecdfcd;
  }
  .wrap {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 24px;
  }
  header.site {
    border-bottom: 1px solid var(--l-line);
  }
  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 64px;
  }
  .bar-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 800;
    font-size: 1.15rem;
    color: var(--l-ink);
    text-decoration: none;
  }
  .brand .accent {
    color: var(--link);
  }
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--brand);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 11px 18px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
  }
  .btn:hover {
    background: var(--brand-hover);
  }
  .btn.ghost {
    background: transparent;
    color: var(--link);
    border: 1px solid var(--l-line);
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .hero {
    padding: 48px 0 8px;
  }
  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-size: 0.72rem;
    font-weight: 800;
    color: var(--link);
    margin: 0 0 12px;
  }
  .hero h1 {
    font-size: clamp(1.9rem, 4vw, 2.6rem);
    letter-spacing: -0.02em;
    margin: 0 0 14px;
  }
  .lead {
    color: var(--l-muted);
    font-size: 1.08rem;
    max-width: 56ch;
  }
  .card {
    background: var(--l-panel);
    border: 1px solid var(--l-line);
    border-radius: 14px;
    padding: 24px;
    margin: 24px 0 48px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.9rem;
    color: var(--l-ink);
    font-weight: 500;
  }
  input,
  textarea {
    padding: 10px 12px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
  }
  .hp {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
  .ok {
    color: var(--ok-ink);
    font-size: 1.1rem;
    margin: 32px 0;
  }
  .err {
    color: var(--err-ink);
    margin: 0;
  }
  .providers a:focus-visible,
  .providers button:focus-visible,
  .providers input:focus-visible,
  .providers textarea:focus-visible {
    outline: 3px solid var(--link);
    outline-offset: 2px;
  }
  #main-content:focus {
    outline: none;
  }
</style>
